import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { type ReactNode, useEffect } from 'react';

import { getConfigOrNull } from '@/lib/config/config-registry';

import { messages as enMessages } from './locales/en.po';

const DEFAULT_LOCALE = 'en';

// Locales must match `lingui.config.ts:locales`. Codes are kept lowercase to
// mirror the set shipped by `wfm.rotaready.com/translations/pre/` so the
// host can pass the same code through `access-evo-change-language` without
// normalisation.
const SUPPORTED_LOCALES = new Set<string>([
  'en',
  'de-de',
  'el',
  'es-es',
  'fr-fr',
  'it-it',
  'nl-nl',
  'ro',
  'sq',
]);

// The source locale is bundled and loaded synchronously so the default
// language renders real strings from first paint. Production builds strip the
// Lingui macro's inline default messages, so an empty `en` catalog surfaces
// raw message IDs (e.g. `US3UTc`) instead of text. Non-default catalogs stay
// lazy — pulled on demand via dynamic `.po` imports (handled by
// `@lingui/vite-plugin`) in `loadAndActivate` below.
i18n.load(DEFAULT_LOCALE, enMessages);
i18n.activate(DEFAULT_LOCALE);

/**
 * Wraps Lingui's `I18nProvider` and listens for the host's
 * `access-evo-change-language` event so the active locale follows the
 * host shell. Replaces the i18next-specific `LanguageSwitcher` removed
 * during Ring 6 (Phase 1 review).
 *
 * On locale change the requested catalog is dynamically imported (Vite
 * splits each `.po` into its own chunk via `@lingui/vite-plugin`),
 * `i18n.load()`-ed, then activated. Unsupported locale codes are
 * ignored so a malformed host event can't park the app in a broken
 * state.
 */
export function Locale({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Follow the host-supplied locale from first load: when embedded (WFM),
    // `config.locale` is the language the host wants. `en` is already the
    // module default, so only switch for a supported non-default locale.
    const hostLocale = normaliseLocale(getConfigOrNull()?.locale);
    if (hostLocale && hostLocale !== DEFAULT_LOCALE) {
      void loadAndActivate(hostLocale);
    }

    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent<{ language: string }>).detail;
      const next = normaliseLocale(detail?.language);
      if (next) void loadAndActivate(next);
    };

    window.addEventListener('access-evo-change-language', handleChange);
    return () => {
      window.removeEventListener('access-evo-change-language', handleChange);
    };
  }, []);

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}

/**
 * Loads the requested catalog before activating it (the order matters —
 * activating without loading silently renders no change), then activates.
 */
async function loadAndActivate(locale: string): Promise<void> {
  const { messages } = await import(`./locales/${locale}.po`);
  i18n.load(locale, messages);
  i18n.activate(locale);
}

/**
 * Maps a host locale code to a supported catalog code, or `null` when none
 * applies. Lowercases the input (hosts may send `en-GB`), accepts an exact
 * supported match, then falls back to the base language (`en-gb` → `en`).
 */
function normaliseLocale(raw: string | undefined): string | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (SUPPORTED_LOCALES.has(lower)) return lower;
  const base = lower.split('-')[0];
  return base && SUPPORTED_LOCALES.has(base) ? base : null;
}
